import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Mail, CheckCircle2, Lock, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Setup Fee gate — shown immediately after a property is created and
 * blocks tenant/vehicle creation until the $99 setup fee is settled
 * (paid via Stripe OR the admin marks an invoice paid).
 *
 * Two CTAs:
 *   • Pay Now      → 1-click Stripe Checkout
 *   • Receive Invoice → auto-issues the first invoice, emails it to the
 *                      billing contact, and flips this screen into a
 *                      "Invoice sent — awaiting payment" status until
 *                      the admin marks the invoice paid.
 */
export default function SetupFee() {
  const { activeProperty, refreshProperties } = useAuth();
  const nav = useNavigate();
  const [paying, setPaying] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const sentAt = activeProperty?.setup_fee_invoice_sent_at;
  const paid = !!(
    activeProperty?.setup_fee_paid_at || activeProperty?.invoice_paid_at
  );

  if (paid) {
    // Belt-and-suspenders: if the user lands here after paying, send
    // them to the onboard wizard so they can start adding tenants.
    setTimeout(() => nav("/dashboard/onboard", { replace: true }), 0);
    return null;
  }

  const payNow = async () => {
    if (paying) return;
    setPaying(true);
    try {
      const { data } = await api.post("/billing/setup-fee", {
        property_id: activeProperty.id,
        origin_url: window.location.origin,
        redirect_after: "onboard",
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(false);
    }
  };

  const requestInvoice = async () => {
    if (requesting) return;
    setRequesting(true);
    try {
      const { data } = await api.post("/billing/setup-fee-invoice", {
        property_id: activeProperty.id,
      });
      toast.success(data.message || "Invoice sent — awaiting payment.");
      await refreshProperties();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-start justify-center pt-16 px-4 pb-16">
      <div className="w-full max-w-xl space-y-6" data-testid="setup-fee-screen">
        <div className="text-center space-y-2">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#0F8F4F]/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-[#0F8F4F]" strokeWidth={1.5} />
          </div>
          <div className="mono-tiny text-[#71717A]">Activate your account</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#09090B] leading-tight">
            One time setup fee
          </h1>
          <p className="text-sm text-[#71717A]">
            Pay your setup fee to start adding tenants and vehicles.
          </p>
        </div>

        <div
          className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 shadow-sm"
          data-testid="setup-fee-card"
        >
          <div className="flex items-baseline justify-between gap-3 pb-4 border-b border-[#E4E4E7]">
            <div>
              <div className="mono-tiny text-[#71717A]">One-time Setup Fee</div>
              <div className="text-xs text-[#71717A] mt-0.5">
                Inkless mini printer · QR sticker supply · welcome packet
              </div>
            </div>
            <div className="font-display font-bold text-3xl text-[#09090B]">
              $99
            </div>
          </div>

          {sentAt ? (
            <div
              className="mt-5 rounded-lg bg-[#FFFBEA] border border-[#FACC15]/40 p-4 flex items-start gap-3"
              data-testid="setup-fee-invoice-sent"
            >
              <Mail
                className="w-5 h-5 text-[#92400E] mt-0.5 shrink-0"
                strokeWidth={1.5}
              />
              <div className="text-sm text-[#7C2D12]">
                <div className="font-bold">Invoice sent — awaiting payment.</div>
                <div className="mt-1 text-[#92400E]">
                  We emailed your invoice. Tenant and vehicle setup unlocks once
                  the admin marks it paid. Changed your mind? You can still{" "}
                  <button
                    type="button"
                    onClick={payNow}
                    className="underline font-medium hover:text-[#7C2D12]"
                    data-testid="setup-fee-pay-now-after-invoice"
                  >
                    pay now with a card
                  </button>
                  .
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              <Button
                onClick={payNow}
                disabled={paying || requesting}
                className="w-full h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B] text-base"
                data-testid="setup-fee-pay-now"
              >
                {paying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening
                    Stripe…
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" /> Pay Now
                  </>
                )}
              </Button>
              <Button
                onClick={requestInvoice}
                disabled={paying || requesting}
                variant="outline"
                className="w-full h-12 border-[#E4E4E7] text-[#09090B] hover:bg-[#F4F4F5] text-base"
                data-testid="setup-fee-receive-invoice"
              >
                {requesting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending
                    invoice…
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" /> Receive Invoice
                  </>
                )}
              </Button>
              <p className="text-xs text-[#71717A] text-center pt-1">
                Pay Now charges your card instantly. Receive Invoice emails
                you the bill — your account activates once it's paid.
              </p>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-lg p-5">
          <div className="mono-tiny text-[#71717A] mb-2">What happens next</div>
          <ul className="text-sm text-[#3F3F46] space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2
                className="w-4 h-4 text-[#0F8F4F] mt-0.5 shrink-0"
                strokeWidth={2}
              />
              Your inkless mini printer ships with QR stickers and welcome packet.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2
                className="w-4 h-4 text-[#0F8F4F] mt-0.5 shrink-0"
                strokeWidth={2}
              />
              You unlock tenant + vehicle setup and can start activating QRs.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2
                className="w-4 h-4 text-[#0F8F4F] mt-0.5 shrink-0"
                strokeWidth={2}
              />
              Monthly billing kicks in at $1.00 per active car after the
              free 30-day window (with volume discounts at 200/500 cars,
              and a custom quote at 1000+).
            </li>
          </ul>
        </div>

        <div className="text-center space-y-2">
          {sentAt ? (
            <Button
              onClick={() => nav("/dashboard")}
              variant="outline"
              className="h-11 px-5 border-[#E4E4E7] text-[#09090B] hover:bg-[#F4F4F5]"
              data-testid="setup-fee-go-to-dashboard"
            >
              Go to Dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : null}
          <div>
            <Link
              to="/dashboard/billing"
              className="text-sm text-[#71717A] hover:text-[#09090B] underline"
              data-testid="setup-fee-view-billing"
            >
              View billing details
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
